// Per spec/blocks.md §4. Monthly recap of day chairs from the prior month.
// First list-shape Inputs at the Block layer (`entries: DayChairEntry[]`)
// + first `autoHide` callback. The orchestrator (P-018+) reads the
// callback and skips render entirely when entries is empty — no
// in-render `EMPTY` short-circuit needed (D-052 stays file-local in
// section-heading.ts).
//
// **P-037.18 D-118.** The `layout` enum is dropped entirely — three-column
// is the only supported render path. Banks's audit: "drop the other two
// layouts, keep 3 column." Persisted templates with the old `layout`
// value are stripped at the v3 → v4 migration step (the new options
// schema is `z.object({})` so Zod's default strip mode discards stray
// keys silently regardless).
//
// **P-037.18 D-118 — per-row dividers.** Each entry sits below a
// 3-segment divider matching the column rhythm `30/190/25/190/25/190/30`
// (same pattern as member-list per D-117). Color `tokens.color['rule-
// subtle']`. Gap above/below the rule = 20 px (system-wide convention,
// shared with chapter-whatsapp / member-list / day-chairs). Implemented
// by the new `gridLayout(..., { rowDivider })` opt in helpers.ts.
//
// **P-037.18 D-118 — optional `memberUrl` per entry.** When set, the
// member name renders as a link in the template-level link color
// (`ctx.linkColor` derived from the Email's Header style). Same pattern
// as member-list's url field (D-117).
//
// **P-037.29 D-137.** `inputs.intro` richText field dropped per Banks's
// "only `text` + `time-location-cost` carry richText" rule. Schema v10
// → v11 migration is a Zod-strip pass-through — old persisted
// instances with the `intro` key parse cleanly and the field
// disappears. Staff composes a separate `text` Block above day-chairs
// when they need intro copy.
//
// See D-059 for the original layout + autoHide-on-empty-entries
// rationale.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { tokens } from '../tokens'
import { dayChairItem, type DayChairItemProps } from '../components/day-chair-item'
import { gridLayout, pickDefined } from '../components/helpers'

const dayChairEntrySchema = z.object({
  memberName: z.string().min(1),
  eventName: z.string().min(1),
  memberPhotoUrl: z.string().url().describe('imageUrl').optional(),
  eventDate: z.string().min(1).optional(),
  // P-037.18 D-118: optional URL — wraps memberName in a link rendered
  // in `ctx.linkColor` (template-level) when present.
  memberUrl: z.string().url().optional(),
})

type DayChairEntry = z.infer<typeof dayChairEntrySchema>

// P-037.18 D-118: layout enum dropped. The schema is now options-free;
// keeping the empty `z.object({})` keeps the BlockDefinition shape
// uniform for the form generator (which iterates optionsSchema fields).
const optionsSchema = z.object({})

const inputsSchema = z.object({
  entries: z.array(dayChairEntrySchema),
})

function entryToProps(entry: DayChairEntry, linkColor: string): DayChairItemProps {
  return {
    memberName: entry.memberName,
    eventName: entry.eventName,
    linkColor,
    ...pickDefined({
      memberPhotoUrl: entry.memberPhotoUrl,
      eventDate: entry.eventDate,
      memberUrl: entry.memberUrl,
    }),
  }
}

export const dayChairs = defineBlock({
  id: 'day-chairs',
  label: 'Day Chairs',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: {
    entries: [],
  },
  autoHide: ({ inputs }) => inputs.entries.length === 0,
  render: ({ inputs }, ctx) => {
    // P-037.18 D-118: per-row dividers above every entry via the
    // `gridLayout(..., { rowDivider })` opt. Matches member-list's
    // pattern from D-117 (3-segment divider w/ gutter gaps + 20 px
    // padY). dayChairItem receives ctx.linkColor for the optional
    // memberUrl link arm.
    const entriesGrid = gridLayout(
      inputs.entries,
      3,
      (e) => dayChairItem(entryToProps(e, ctx.linkColor)),
      { rowDivider: { color: tokens.color['rule-subtle'], padY: 20 } },
    )
    // P-037.14: 30 px L/R padding on the content row. The baked
    // SectionHeader was dropped at P-037.20 / D-126 + the optional
    // `intro` richText row was dropped at P-037.29 / D-137 — users
    // compose `section-heading` + `text` Blocks above this one when
    // they want a heading and/or intro copy. Inner content lives at
    // the (680 − 60) = 620 px content area.
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="padding: 0 30px;">${entriesGrid}</td></tr></table>`
  },
})
